import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Slider } from '../../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  GitBranch, 
  Plus,
  Edit,
  Trash2,
  Shield,
  Loader2,
  RefreshCw,
  Lock,
  AlertTriangle,
  Link as LinkIcon,
  Search,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { getUserErrorMessage } from '../../utils/errorMessages';

const AdminMappings = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [mappings, setMappings] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    risk_name: '',
    control_ids: [],
    weight: 1.0,
    notes: ''
  });
  
  // Available controls for selected framework
  const [availableControls, setAvailableControls] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchMappings();
  }, [selectedFramework]);

  useEffect(() => {
    // Update available controls when framework changes for create/edit
    if (selectedFramework && selectedFramework !== 'all') {
      const fw = frameworks.find(f => f.id === selectedFramework);
      setAvailableControls(fw?.controls || []);
    } else {
      setAvailableControls([]);
    }
  }, [selectedFramework, frameworks]);

  const fetchData = async () => {
    try {
      const frameworksRes = await api.get('/admin/frameworks');
      setFrameworks(frameworksRes.data);
      await fetchMappings();
    } catch (error) {
      toast.error('Data laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  const fetchMappings = async () => {
    try {
      const params = selectedFramework !== 'all' ? `?framework_id=${selectedFramework}` : '';
      const response = await api.get(`/admin/mappings${params}`);
      setMappings(response.data);
    } catch (error) {
      console.error('Error fetching mappings:', error);
    }
  };

  const handleCreateMapping = async () => {
    if (!formData.risk_name || formData.control_ids.length === 0) {
      toast.error('Vul risico naam in en selecteer minimaal één control');
      return;
    }

    if (selectedFramework === 'all') {
      toast.error('Selecteer eerst een framework');
      return;
    }

    setSaving(true);
    try {
      await api.post(`/admin/mappings?framework_id=${selectedFramework}`, {
        risk_name: formData.risk_name,
        control_ids: formData.control_ids,
        weight: formData.weight,
        notes: formData.notes || null
      });
      toast.success('Mapping aangemaakt');
      setShowCreateModal(false);
      resetForm();
      fetchMappings();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Aanmaken mislukt'));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateMapping = async () => {
    if (!selectedMapping) return;

    setSaving(true);
    try {
      await api.patch(`/admin/mappings/${selectedMapping.id}`, {
        control_ids: formData.control_ids,
        weight: formData.weight,
        notes: formData.notes || null
      });
      toast.success('Mapping bijgewerkt');
      setShowEditModal(false);
      fetchMappings();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Bijwerken mislukt'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMapping = async (mappingId) => {
    if (mappingId.startsWith('builtin_')) {
      toast.error('Ingebouwde mappings kunnen niet worden verwijderd');
      return;
    }

    if (!window.confirm('Weet u zeker dat u deze mapping wilt verwijderen?')) {
      return;
    }

    try {
      await api.delete(`/admin/mappings/${mappingId}`);
      toast.success('Mapping verwijderd');
      fetchMappings();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Verwijderen mislukt'));
    }
  };

  const openEditMapping = (mapping) => {
    if (mapping.is_builtin) {
      toast.error('Ingebouwde mappings kunnen niet worden bewerkt');
      return;
    }
    
    setSelectedMapping(mapping);
    setFormData({
      risk_name: mapping.risk_name,
      control_ids: mapping.control_ids || [],
      weight: mapping.weight || 1.0,
      notes: mapping.notes || ''
    });
    
    // Set available controls from the mapping's framework
    const fw = frameworks.find(f => f.id === mapping.framework_id);
    setAvailableControls(fw?.controls || []);
    
    setShowEditModal(true);
  };

  const openCreateMapping = () => {
    if (selectedFramework === 'all') {
      toast.error('Selecteer eerst een framework om een mapping toe te voegen');
      return;
    }
    resetForm();
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      risk_name: '',
      control_ids: [],
      weight: 1.0,
      notes: ''
    });
  };

  const toggleControlSelection = (controlId) => {
    setFormData(prev => ({
      ...prev,
      control_ids: prev.control_ids.includes(controlId)
        ? prev.control_ids.filter(id => id !== controlId)
        : [...prev.control_ids, controlId]
    }));
  };

  const getFrameworkName = (frameworkId) => {
    const fw = frameworks.find(f => f.id === frameworkId);
    return fw ? `${fw.name} ${fw.version}` : frameworkId;
  };

  const getControlName = (controlId, frameworkId) => {
    const fw = frameworks.find(f => f.id === frameworkId);
    const control = fw?.controls?.find(c => c.id === controlId);
    return control ? `${control.code} - ${control.name}` : controlId;
  };

  // Filter mappings by search
  const filteredMappings = mappings.filter(mapping => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      mapping.risk_name.toLowerCase().includes(query) ||
      mapping.control_ids?.some(id => id.toLowerCase().includes(query))
    );
  });

  // Group mappings by framework for display
  const groupedMappings = filteredMappings.reduce((acc, mapping) => {
    const fwId = mapping.framework_id;
    if (!acc[fwId]) {
      acc[fwId] = [];
    }
    acc[fwId].push(mapping);
    return acc;
  }, {});

  // Stats
  const stats = {
    total: mappings.length,
    builtin: mappings.filter(m => m.is_builtin).length,
    custom: mappings.filter(m => !m.is_builtin).length
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-black">
              Mappings
            </h1>
            <p className="text-sm text-neutral-600 mt-1">
              Koppel risico's aan framework controls voor automatische aanbevelingen
            </p>
          </div>
          <Button 
            className="bg-black text-white hover:bg-neutral-800"
            onClick={openCreateMapping}
            disabled={selectedFramework === 'all'}
            data-testid="create-mapping-button"
          >
            <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />
            Nieuwe Mapping
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <GitBranch className="h-5 w-5 text-blue-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{stats.total}</p>
                  <p className="text-xs text-neutral-500">Totaal mappings</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Lock className="h-5 w-5 text-purple-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{stats.builtin}</p>
                  <p className="text-xs text-neutral-500">Ingebouwd</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <LinkIcon className="h-5 w-5 text-green-600" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{stats.custom}</p>
                  <p className="text-xs text-neutral-500">Custom</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <div className="w-64">
            <Select value={selectedFramework} onValueChange={setSelectedFramework}>
              <SelectTrigger data-testid="framework-filter">
                <Filter className="h-4 w-4 mr-2 text-neutral-400" strokeWidth={1.5} />
                <SelectValue placeholder="Filter op framework" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle frameworks</SelectItem>
                {frameworks.map((fw) => (
                  <SelectItem key={fw.id} value={fw.id}>
                    {fw.name} {fw.version}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" strokeWidth={1.5} />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Zoek op risico naam of control..."
                className="pl-10"
                data-testid="mapping-search"
              />
            </div>
          </div>
          <Button
            variant="outline"
            className="border-neutral-200"
            onClick={fetchMappings}
          >
            <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>

        {/* Info Banner */}
        {selectedFramework === 'all' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-medium text-blue-800">Selecteer een framework</p>
                <p className="text-xs text-blue-600 mt-1">
                  Om een nieuwe mapping toe te voegen, selecteer eerst een framework uit de filter.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Mappings Table */}
        <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-400">
              Mappings ({filteredMappings.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filteredMappings.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="mappings-table">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="text-left text-xs font-medium text-neutral-500 p-4">Risico</th>
                      <th className="text-left text-xs font-medium text-neutral-500 p-4">Framework</th>
                      <th className="text-left text-xs font-medium text-neutral-500 p-4">Controls</th>
                      <th className="text-center text-xs font-medium text-neutral-500 p-4">Type</th>
                      <th className="text-center text-xs font-medium text-neutral-500 p-4">Gewicht</th>
                      <th className="text-right text-xs font-medium text-neutral-500 p-4">Acties</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {filteredMappings.map((mapping) => (
                      <tr key={mapping.id} className="hover:bg-neutral-50" data-testid={`mapping-row-${mapping.id}`}>
                        <td className="p-4">
                          <p className="text-sm font-medium text-black">{mapping.risk_name}</p>
                          {mapping.notes && (
                            <p className="text-xs text-neutral-500 mt-1 line-clamp-1">{mapping.notes}</p>
                          )}
                        </td>
                        <td className="p-4">
                          <Badge variant="outline" className="border-neutral-200 text-neutral-600">
                            {getFrameworkName(mapping.framework_id)}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {mapping.control_ids?.slice(0, 3).map((controlId) => (
                              <Badge
                                key={controlId}
                                variant="outline"
                                className="border-neutral-300 text-neutral-600 text-xs"
                              >
                                {controlId}
                              </Badge>
                            ))}
                            {mapping.control_ids?.length > 3 && (
                              <Badge variant="outline" className="border-neutral-200 text-neutral-400 text-xs">
                                +{mapping.control_ids.length - 3} meer
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          {mapping.is_builtin ? (
                            <Badge className="border-purple-200 bg-purple-50 text-purple-700 text-xs">
                              <Lock className="h-3 w-3 mr-1" strokeWidth={1.5} />
                              Ingebouwd
                            </Badge>
                          ) : (
                            <Badge className="border-green-200 bg-green-50 text-green-700 text-xs">
                              Custom
                            </Badge>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          <span className="text-sm text-neutral-600">{mapping.weight || 1.0}x</span>
                        </td>
                        <td className="p-4 text-right">
                          {!mapping.is_builtin && (
                            <div className="flex gap-1 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => openEditMapping(mapping)}
                              >
                                <Edit className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                                onClick={() => handleDeleteMapping(mapping.id)}
                              >
                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-16 text-center">
                <GitBranch className="h-12 w-12 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-lg font-medium text-black mb-1">Geen mappings gevonden</h3>
                <p className="text-sm text-neutral-500">
                  {searchQuery ? 'Probeer een andere zoekterm' : 'Voeg een mapping toe om te beginnen'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Mapping Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Nieuwe Mapping</DialogTitle>
            <DialogDescription>
              Koppel een risico aan framework controls ({getFrameworkName(selectedFramework)})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="risk_name">Risico Naam</Label>
              <Input
                id="risk_name"
                value={formData.risk_name}
                onChange={(e) => setFormData({ ...formData, risk_name: e.target.value })}
                placeholder="bijv. Phishing kwetsbaarheid"
                data-testid="mapping-risk-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Gewicht ({formData.weight}x)</Label>
              <Slider
                value={[formData.weight]}
                onValueChange={(v) => setFormData({ ...formData, weight: v[0] })}
                min={0.1}
                max={5}
                step={0.1}
                className="py-2"
              />
              <p className="text-xs text-neutral-500">
                Hoger gewicht = meer impact op compliance score
              </p>
            </div>

            <div className="space-y-2">
              <Label>Selecteer Controls ({formData.control_ids.length} geselecteerd)</Label>
              <div className="border border-neutral-200 rounded-lg max-h-48 overflow-y-auto">
                {availableControls.length > 0 ? (
                  availableControls.map((control) => {
                    const isSelected = formData.control_ids.includes(control.id);
                    return (
                      <button
                        key={control.id}
                        type="button"
                        onClick={() => toggleControlSelection(control.id)}
                        className={`w-full flex items-center gap-3 p-3 text-left border-b border-neutral-100 last:border-0 transition-colors ${
                          isSelected ? 'bg-green-50' : 'hover:bg-neutral-50'
                        }`}
                        data-testid={`control-option-${control.id}`}
                      >
                        <div className={`h-6 w-6 rounded flex items-center justify-center ${
                          isSelected ? 'bg-green-500' : 'bg-neutral-200'
                        }`}>
                          {isSelected && (
                            <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-black">{control.code}</p>
                          <p className="text-xs text-neutral-500">{control.name}</p>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="p-4 text-center text-neutral-500 text-sm">
                    Geen controls beschikbaar
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notities (optioneel)</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Extra informatie over deze mapping..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Annuleren
            </Button>
            <Button 
              className="bg-black text-white hover:bg-neutral-800"
              onClick={handleCreateMapping}
              disabled={saving}
              data-testid="create-mapping-submit"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Mapping Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Mapping Bewerken</DialogTitle>
            <DialogDescription>
              {formData.risk_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Gewicht ({formData.weight}x)</Label>
              <Slider
                value={[formData.weight]}
                onValueChange={(v) => setFormData({ ...formData, weight: v[0] })}
                min={0.1}
                max={5}
                step={0.1}
                className="py-2"
              />
            </div>

            <div className="space-y-2">
              <Label>Selecteer Controls ({formData.control_ids.length} geselecteerd)</Label>
              <div className="border border-neutral-200 rounded-lg max-h-48 overflow-y-auto">
                {availableControls.map((control) => {
                  const isSelected = formData.control_ids.includes(control.id);
                  return (
                    <button
                      key={control.id}
                      type="button"
                      onClick={() => toggleControlSelection(control.id)}
                      className={`w-full flex items-center gap-3 p-3 text-left border-b border-neutral-100 last:border-0 transition-colors ${
                        isSelected ? 'bg-green-50' : 'hover:bg-neutral-50'
                      }`}
                    >
                      <div className={`h-6 w-6 rounded flex items-center justify-center ${
                        isSelected ? 'bg-green-500' : 'bg-neutral-200'
                      }`}>
                        {isSelected && (
                          <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-black">{control.code}</p>
                        <p className="text-xs text-neutral-500">{control.name}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notities</Label>
              <Textarea
                id="edit-notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Annuleren
            </Button>
            <Button 
              className="bg-black text-white hover:bg-neutral-800"
              onClick={handleUpdateMapping}
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Opslaan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminMappings;
